import { cn } from '@/platform/lib/utils';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { Children, isValidElement } from 'react';
import ModalActions from './ModalActions';
import ModalContent from './ModalContent';
import ModalHeader from './ModalHeader';

const sizes = {
    mini: 'sm:max-w-xs',
    tiny: 'sm:max-w-sm',
    small: 'sm:max-w-md',
    medium: 'sm:max-w-lg',
    large: 'sm:max-w-3xl',
    fullscreen: 'sm:max-w-[95vw]',
};

const iconVariants = {
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-amber-100 text-amber-600',
    success: 'bg-green-100 text-green-600',
    info: 'bg-primary-100 text-primary-600',
};

// `icon` turns the modal into an alert/confirmation: any svg element (a
// heroicon) rendered in a coloured bubble beside the header and content. It
// inherits its colour from the bubble, so pass it without a className.
const Modal = ({
    open = false,
    onClose,
    size = 'large',
    icon,
    iconVariant = 'info',
    iconPosition = 'left',
    className,
    children,
    ...props
}) => {
    // Actions stay full width underneath; everything else stacks in the column
    // next to the icon instead of wrapping around underneath it.
    const items = Children.toArray(children);
    const actions = items.filter(
        child => isValidElement(child) && child.type === ModalActions
    );
    const body = items.filter(child => !actions.includes(child));

    const bubble = icon && (
        <div
            aria-hidden='true'
            className={cn(
                'mx-auto flex size-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10 [&>svg]:size-6',
                iconVariants[iconVariant] ?? iconVariants.info
            )}
        >
            {icon}
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose ?? (() => {})}
            className='relative z-50'
        >
            <DialogBackdrop
                transition
                className={cn(
                    'fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition',
                    'data-closed:opacity-0',
                    'data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in'
                )}
            />
            <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
                <div className='flex min-h-full items-end justify-center p-4 sm:items-center sm:p-6'>
                    <DialogPanel
                        transition
                        className={cn(
                            'relative flex w-full flex-col gap-4 rounded-xl bg-white p-4 text-left shadow-xl outline-1 -outline-offset-1 outline-gray-900/5 transition sm:my-8 sm:p-6',
                            'data-closed:translate-y-4 data-closed:opacity-0 data-closed:sm:translate-y-0 data-closed:sm:scale-95',
                            'data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in',
                            sizes[size] ?? sizes.large,
                            className
                        )}
                        {...props}
                    >
                        {icon ? (
                            <div
                                className={cn(
                                    'flex flex-col gap-3 sm:items-start sm:gap-4',
                                    iconPosition === 'right'
                                        ? 'sm:flex-row-reverse'
                                        : 'sm:flex-row'
                                )}
                            >
                                {bubble}
                                <div className='flex flex-col gap-2 text-center sm:text-left'>
                                    {body}
                                </div>
                            </div>
                        ) : (
                            body
                        )}
                        {actions}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Actions = ModalActions;

export default Modal;
